<script setup lang="ts">
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const title = t("legalPages.familyCard.title");
const description = t("legalPages.familyCard.description");
</script>

<template>
  <Title>{{ title }}</Title>
  <Meta name="description" :content="description" />
  <div class="landing">
    <div class="container">
      <h1>Reglamento del Family Club</h1>

      <div class="section">
        <div class="section-title">
          <span class="section-number">1</span>
          Objetivo
        </div>
        <div class="section-content">
          <p>
            El Club de Fidelidad Family Club de Toys"R"Us y Prenatal tiene como
            propósito reconocer y premiar la preferencia de nuestros clientes,
            ofreciéndoles beneficios exclusivos, promociones especiales y
            experiencias únicas por su lealtad y constancia en la compra de
            nuestros productos y servicios.
          </p>
          <div class="highlight">
            La tarjeta Family Card es propiedad exclusiva de PRG RETAIL GROUP
            SPAIN S.L.U.
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">2</span>
          Definiciones
        </div>
        <div class="section-content">
          <p>
            <strong>Socio:</strong> Persona física mayor de 18 años que se ha
            inscrito correctamente en el Club de Fidelidad.
          </p>
          <p>
            <strong>Beneficios:</strong> Productos, servicios, descuentos,
            experiencias u otros premios ofrecidos a los socios.
          </p>
          <p>
            <strong>Cuenta:</strong> Registro individual de cada socio donde se
            reflejan sus puntos y movimientos.
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">3</span>
          Afiliación
        </div>
        <div class="section-content">
          <p>La afiliación es <strong>gratuita y voluntaria</strong>.</p>
          <p>
            Para afiliarse, el interesado debe completar el formulario de
            inscripción en tienda física, sitio web o aplicación móvil,
            proporcionando datos personales verídicos y actualizados.
          </p>
          <p>
            La empresa se reserva el derecho de aceptar o rechazar solicitudes
            de afiliación sin necesidad de justificación.
          </p>
          <p>
            Cada persona solo podrá tener una cuenta activa. En caso de detectar
            duplicidad, se procederá a la unificación de las cuentas.
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">6</span>
          Canje de Beneficios
        </div>
        <div class="section-content">
          <p>
            Los descuentos, cupones o bonos podrán ser canjeados por los
            beneficios disponibles y vigentes para los miembros del Club, sujeto
            a disponibilidad y condiciones específicas de cada descuento, bono o
            promoción.
          </p>
          <p>
            El listado de beneficios podrá ser modificado periódicamente, sin
            previo aviso. En todo momento, el cliente será informado acerca de
            los cambios realizados a través de nuestras habituales vías de
            comunicación: página web familycard.es, email o tienda física.
          </p>
          <div class="highlight">
            El canje de descuentos, cupones o bonos es personal e
            intransferible.
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">7</span>
          Vigencia
        </div>
        <div class="section-content">
          <p>
            En caso de cancelación de la suscripción, los descuentos, cupones o
            bonos acumulados se perderán de forma definitiva.
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">8</span>
          Responsabilidades y Obligaciones del Socio
        </div>
        <div class="section-content">
          <ul>
            <li>Mantener actualizados sus datos personales y de contacto.</li>
            <li>Hacer uso correcto y personal de la membresía.</li>
            <li>
              Notificar de inmediato cualquier uso no autorizado o sospecha de
              fraude en su cuenta.
            </li>
            <li>
              Respetar los términos y condiciones del Family Club, así como las
              políticas de la empresa.
            </li>
          </ul>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">9</span>
          Suspensión y Cancelación de la Membresía
        </div>
        <div class="section-content">
          <p>
            La empresa podrá suspender o cancelar la membresía de cualquier
            socio en caso de:
          </p>
          <ul>
            <li>Uso indebido o fraudulento del programa.</li>
            <li>Proporcionar información falsa o incompleta.</li>
            <li>
              Incumplimiento de este reglamento o de las políticas de la
              empresa.
            </li>
          </ul>
          <p>
            En caso de suspensión o cancelación, los descuentos, cupones o bonos
            serán cancelados y no podrán ser reclamados.
          </p>
          <p>
            En caso de robo o extravío de la tarjeta, el titular deberá
            comunicarlo de inmediato a PRG RETAIL GROUP SPAIN S.L.U.,
            dirigiéndose a cualquier tienda Toys"R"Us o Prénatal o bien por
            escrito a:
          </p>
          <div class="contact-info">
            <strong>PRG RETAIL GROUP SPAIN S.L.U.</strong><br />
            Avenida de Aragón 402, Edificio Allende, 6ª planta<br />
            28022 Madrid
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">10</span>
          Modificaciones al Reglamento
        </div>
        <div class="section-content">
          <p>
            La empresa se reserva el derecho de modificar, suspender o cancelar
            el Family Club, sus beneficios, mecánicas y este reglamento en
            cualquier momento. Cualquiera de las informaciones anteriores, serán
            debidamente informados a los clientes con las medidas adoptadas a
            tomar.
          </p>
          <p>
            Cualquier cambio será comunicado a los socios a través de los
            canales oficiales (correo electrónico, página web, redes sociales,
            etc.).
          </p>
          <p>
            La participación continua en el Family Club implica la aceptación de
            las modificaciones realizadas.
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">11</span>
          Protección de Datos Personales
        </div>
        <div class="section-content">
          <p>
            Los datos personales proporcionados serán tratados conforme a la
            legislación vigente en materia de protección de datos y únicamente
            para los fines relacionados con el Family Club.
          </p>
          <p>
            El socio podrá ejercer sus derechos de información, acceso,
            rectificación, oposición, cancelación, supresión, limitación de
            tratamiento y/o portabilidad enviando una solicitud a la dirección
            de contacto indicada por la empresa:
            <strong>rgpd@toysrus.es</strong>
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">12</span>
          Contacto y Atención a Socios
        </div>
        <div class="section-content">
          <p>
            Para cualquier duda, aclaración o reclamación, los socios podrán
            comunicarse al centro de atención al cliente, disponible en los
            canales oficiales de la empresa.
          </p>
          <p>
            El horario de atención y los medios de contacto estarán disponibles
            en la página web y en los establecimientos participantes.
          </p>
        </div>
      </div>

      <div class="section">
        <div class="section-title">
          <span class="section-number">13</span>
          Aceptación de Términos
        </div>
        <div class="section-content">
          <p>
            La inscripción y participación en el Family Club implica la
            aceptación total de este reglamento, de sus normas y de las
            políticas de PRG RETAIL GROUP SPAIN S.L.U.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.landing body {
  font-family: 'Segoe UI', Arial, sans-serif;
  background: #f7f7fa;
  margin: 0;
  padding: 0;
  color: #222;
  line-height: 1.6;
}
.landing .container {
  max-width: 800px;
  margin: 40px auto;
  background: fff;
  border-radius: 14px;
  box-shadow: 0 4px 24px rgba(100,92,140,0.08);
  padding: 40px 32px;
}
.landing h1 {
  color: rgb(100 92 140);
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 40px;
  border-bottom: 3px solid rgb(100 92 140);
  padding-bottom: 20px;
}
.landing .section {
  margin-bottom: 32px;
  background: #edeaf6;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(100,92,140,0.06);
}
.landing .section-title {
  color: rgb(100 92 140);
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}
.landing .section-number {
  background: rgb(100 92 140);
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  margin-right: 12px;
  font-size: 1rem;
}
.landing .section-content {
  font-size: 1.05rem;
  color: #333;
  line-height: 1.7;
}
.landing .section-content p {
  margin-bottom: 12px;
}
.landing .section-content ul {
  margin: 12px 0;
  padding-left: 20px;
}
.landing .section-content li {
  margin-bottom: 8px;
}
.landing .highlight {
  background: rgba(100,92,140,0.1);
  padding: 16px;
  border-left: 4px solid rgb(100 92 140);
  border-radius: 6px;
  margin: 16px 0;
}
.landing .contact-info {
  background: rgb(100 92 140);
  color: white;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
}
@media (max-width: 600px) {
  .landing .container { 
    padding: 24px 20px; 
    margin: 20px 16px;
  }
  .landing h1 { font-size: 2rem; }
  .section-title { font-size: 1.2rem; }
  .section { padding: 20px; }
}
</style>
